import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { graphqlApi } from '../../../services/graphql';
import { useAuthStore } from '../../../store/authStore';
import { Spinner } from '../../common/Spinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ChannelIcon from '../../campaigns/ChannelIcon';
import { generateCampaignContractPdf } from '../../../utils/campaignContractPdf';

const CollaborationAcceptPage: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [match, setMatch] = useState<any>(null);
  const [campaign, setCampaign] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedMissions, setSelectedMissions] = useState<string[]>([]);
  const [visitStartDate, setVisitStartDate] = useState('');
  const [visitEndDate, setVisitEndDate] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [notes, setNotes] = useState('');
  const [hostComment, setHostComment] = useState('');

  const fetchData = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    try {
      const matchRes = await graphqlApi.getMatch(matchId);
      if (matchRes.success && matchRes.data) {
        setMatch(matchRes.data);
        const campRes = await graphqlApi.getCampaign(matchRes.data.campaignId);
        if (campRes.success && campRes.data) {
          setCampaign(campRes.data);
          const c = campRes.data as any;
          // Use match's visit info if available, fallback to campaign
          const m = matchRes.data as any;
          setVisitStartDate(m.visitInfo?.startDate || c.visitStartDate || '');
          setVisitEndDate(m.visitInfo?.endDate || c.visitEndDate || '');
          if (m.visitInfo?.adults != null) setAdults(m.visitInfo.adults);
          if (m.visitInfo?.children != null) setChildren(m.visitInfo.children);
          if (m.visitInfo?.infants != null) setInfants(m.visitInfo.infants);
          // Use match's selectedMissions (what influencer chose during apply)
          if (m.selectedMissions?.length > 0) {
            setSelectedMissions(m.selectedMissions);
          } else {
            // Fallback to all campaign channels
            const channels: string[] = [];
            const pc = c.publishingConditions;
            if (pc?.instagram?.conditions?.length > 0) channels.push('instagram');
            if (pc?.blog?.conditions?.length > 0) channels.push('blog');
            if (pc?.youtube?.conditions?.length > 0) channels.push('youtube');
            setSelectedMissions(channels);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load:', err);
      toast.error('데이터를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAccept = async () => {
    if (!matchId) return;
    setSubmitting(true);
    try {
      const res = await graphqlApi.acceptMatch(matchId, {
        selectedMissions,
        visitInfo: {
          startDate: visitStartDate,
          endDate: visitEndDate,
          adults,
          children,
          infants,
        },
        acceptanceNotes: notes || undefined,
      });
      if (res.success) {
        toast.success('협업 요청을 수락했습니다');
        navigate('/my-profile/campaigns');
      } else {
        toast.error(res.error || '수락에 실패했습니다');
      }
    } catch (err: any) {
      toast.error(err.message || '수락에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = async () => {
    if (!matchId || !rejectReason.trim()) {
      toast.error('불가사유를 입력해주세요');
      return;
    }
    setSubmitting(true);
    try {
      const res = await graphqlApi.rejectMatch(matchId, rejectReason.trim());
      if (res.success) {
        toast.success('참여를 거부했습니다');
        setShowRejectModal(false);
        navigate('/my-profile/campaigns');
      } else {
        toast.error(res.error || '거부에 실패했습니다');
      }
    } catch (err: any) {
      toast.error(err.message || '거부에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadContract = () => {
    if (!campaign || !user) return;
    generateCampaignContractPdf({
      campaign,
      userName: user.name,
      selectedMissions,
      visitStartDate,
      visitEndDate,
      requestedAt: match?.requestedAt,
      respondedAt: match?.respondedAt || new Date().toISOString(),
    });
  };

  if (loading) return <div className="flex justify-center items-center min-h-64"><Spinner /></div>;
  if (!match || !campaign) return <div className="max-w-3xl mx-auto px-4 py-12 text-center text-gray-400">데이터를 찾을 수 없습니다.</div>;

  const c = campaign as any;
  const pc = c.publishingConditions;
  const availableChannels: { key: string; label: string; conditions: { item: string; quantity: number }[]; extra?: string }[] = [];
  if (pc?.instagram?.conditions?.length > 0) availableChannels.push({
    key: 'instagram', label: '인스타그램', conditions: pc.instagram.conditions,
    extra: pc.instagram.collaboratorId ? `공동작업자: ${pc.instagram.collaboratorId}` : undefined,
  });
  if (pc?.blog?.conditions?.length > 0) availableChannels.push({
    key: 'blog', label: '블로그', conditions: pc.blog.conditions,
    extra: pc.blog.insertLink ? `삽입링크: ${pc.blog.insertLink}` : undefined,
  });
  if (pc?.youtube?.conditions?.length > 0) availableChannels.push({
    key: 'youtube', label: '유튜브', conditions: pc.youtube.conditions,
    extra: pc.youtube.insertLink ? `삽입링크: ${pc.youtube.insertLink}` : undefined,
  });

  const conditionItemLabel = (item: string) => {
    const labels: Record<string, string> = {
      reels: '릴스', story: '스토리', post: '포스트/피드', feed: '피드',
      blogPost: '블로그 포스트', video: '영상', shorts: '쇼츠',
    };
    return labels[item] || item;
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <button onClick={() => navigate('/my-profile/campaigns')} className="flex items-center text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="w-4 h-4 mr-1" /> 내 캠페인
      </button>

      <div>
        <h1 className="text-xl font-bold text-gray-900">캠페인 진행확정</h1>
        <p className="text-sm text-gray-500 mt-1">캠페인의 인플루언서로 선정되셨습니다.<br/>아래 캠페인 조건을 확인하고 진행 의사를 선택해주세요.</p>
      </div>
      <hr className="border-gray-200" />

      {/* Campaign Info */}
      <div className="space-y-3">
        <div><span className="text-sm text-gray-500">캠페인 명</span><p className="text-base font-semibold text-gray-900">{c.campaignName}</p></div>
        <div>
          <span className="text-sm text-gray-500">캠페인 미션</span>
          <div className="mt-2 space-y-3">
            {availableChannels.filter(ch => selectedMissions.includes(ch.key)).map(ch => (
              <div key={ch.key} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex-1">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                    <ChannelIcon channel={ch.key} className="w-4 h-4" />
                    {ch.label}
                  </span>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {ch.conditions.map((cond: any, idx: number) => (
                      <span key={idx} className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                        {conditionItemLabel(cond.item)} {cond.quantity}건
                      </span>
                    ))}
                  </div>
                  {ch.extra && <p className="mt-1 text-xs text-gray-400">{ch.extra}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <hr className="border-gray-200" />

      {/* Visit Info */}
      <div className="space-y-4">
        <h2 className="text-base font-semibold text-gray-900">방문정보</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 w-32">방문 가능 일정</span>
          <span className="text-sm text-gray-900">{visitStartDate || '-'}</span>
          <span className="text-gray-400">~</span>
          <span className="text-sm text-gray-900">{visitEndDate || '-'}</span>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm text-gray-600 w-32">방문인원</span>
          <span className="text-sm text-gray-700">성인 <span className="font-medium">{adults}</span>명</span>
          <span className="text-sm text-gray-700">아동 <span className="font-medium">{children}</span>명</span>
          <span className="text-sm text-gray-700">유아 <span className="font-medium">{infants}</span>명</span>
        </div>
      </div>
      <hr className="border-gray-200" />

      {/* Contract */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">온라인 계약서</span>
          <button onClick={handleDownloadContract} className="px-4 py-1.5 text-xs font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">계약서 미리보기</button>
        </div>
        <p className="text-xs text-gray-500">요청 수락을 눌러주시면 자동으로 계약이 성사되며, 해당 내용으로 계약서가 이메일로 발송됩니다.</p>
      </div>
      <hr className="border-gray-200" />

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button onClick={handleAccept} disabled={submitting} className="flex-1 py-3 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors">{submitting ? '처리 중...' : '확정 진행'}</button>
      </div>

      {/* Reject Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowRejectModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div>
              <span className="text-xs text-gray-400">캠페인 명</span>
              <p className="text-sm font-semibold text-gray-900">{c.campaignName}</p>
            </div>
            <hr className="border-gray-200" />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">불가사유</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                rows={4}
                placeholder="참여 불가 사유를 입력해주세요"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowRejectModal(false)} className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-300 rounded-xl hover:bg-gray-50">취소</button>
              <button onClick={handleReject} disabled={submitting || !rejectReason.trim()} className="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50">{submitting ? '처리 중...' : '전송'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborationAcceptPage;
